import { useState, useMemo } from 'react';
import { Check, UserPlus, Loader2, Search, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { AdminTabShell } from './AdminTabShell';
import type { PendingUser, AdminUser } from '@/hooks/admin/useAdminPendingUsers';

interface Props {
  data: PendingUser[] | null;
  isLoading: boolean;
  error: Error | null;
  onApproved: () => void;
  allUsers?: AdminUser[] | null;
  allUsersLoading?: boolean;
}

async function invokeApprove(userId: string): Promise<{ success?: boolean; error?: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('No session');
  const { data, error } = await supabase.functions.invoke('user-approval', {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.access_token}` },
    body: { userId },
  });
  if (error) throw error;
  return data as { success?: boolean; error?: string };
}

type ViewMode = 'pending' | 'all';

export function UserApprovalTab({ data, isLoading, error, onApproved, allUsers, allUsersLoading }: Props) {
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();

  const handleApprove = async (userId: string, email: string | null) => {
    setApprovingId(userId);
    try {
      const result = await invokeApprove(userId);
      if (result?.success) {
        toast({ title: 'User approved', description: email ?? userId });
        onApproved();
      } else {
        toast({ title: 'Approval failed', description: result?.error ?? 'Unknown error', variant: 'destructive' });
      }
    } catch (err) {
      console.error('Approve error:', err);
      toast({ title: 'Approval failed', description: 'Could not approve user', variant: 'destructive' });
    } finally {
      setApprovingId(null);
    }
  };

  const filteredUsers = useMemo(() => {
    if (!allUsers) return [];
    if (!searchQuery.trim()) return allUsers;
    const q = searchQuery.toLowerCase();
    return allUsers.filter((u) =>
      (u.email?.toLowerCase().includes(q)) ||
      (u.full_name?.toLowerCase().includes(q)) ||
      (u.org_name?.toLowerCase().includes(q)) ||
      (u.role?.toLowerCase().includes(q))
    );
  }, [allUsers, searchQuery]);

  const pendingCount = data?.length ?? 0;
  const allCount = allUsers?.length ?? 0;

  return (
    <AdminTabShell isLoading={isLoading} error={error}>
      {/* View toggle */}
      <div className="flex items-center gap-2 mb-4">
        <Button
          variant={viewMode === 'pending' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('pending')}
          className="gap-1.5 text-xs"
        >
          <UserPlus className="h-3 w-3" />
          Pending
          {pendingCount > 0 && (
            <span className="ml-1 inline-flex items-center justify-center h-4 min-w-[1rem] rounded-full bg-wt-amber-primary text-[10px] font-bold text-wt-bg-primary px-1">
              {pendingCount}
            </span>
          )}
        </Button>
        <Button
          variant={viewMode === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('all')}
          className="gap-1.5 text-xs"
        >
          <Users className="h-3 w-3" />
          All Users
          <span className="ml-1 text-[10px] opacity-60">{allCount}</span>
        </Button>
      </div>

      {viewMode === 'pending' && (
        <>
          {!data || data.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-wt-text-muted">
              <UserPlus className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm">No pending signups</p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-wt-text-muted mb-4">
                {data.length} user{data.length !== 1 ? 's' : ''} pending approval
              </p>
              <div className="rounded-lg border border-wt-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-wt-bg-secondary text-wt-text-muted text-xs uppercase tracking-wider">
                      <th className="text-left px-4 py-2 font-medium">Email</th>
                      <th className="text-left px-4 py-2 font-medium">Signed Up</th>
                      <th className="text-right px-4 py-2 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((u) => (
                      <tr key={u.user_id} className="border-t border-wt-border hover:bg-wt-bg-secondary/50">
                        <td className="px-4 py-3 font-mono text-wt-text-primary">
                          {u.email ?? u.user_id}
                        </td>
                        <td className="px-4 py-3 text-wt-text-muted">
                          {u.created_at ? new Date(u.created_at).toLocaleString() : '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            onClick={() => handleApprove(u.user_id, u.email)}
                            disabled={approvingId === u.user_id}
                            className="gap-1.5"
                          >
                            {approvingId === u.user_id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Check className="h-3.5 w-3.5" />
                            )}
                            Approve
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {viewMode === 'all' && (
        <>
          {/* Search */}
          <div className="relative mb-4 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-wt-text-muted" />
            <Input
              placeholder="Search by email, name, org, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 text-sm"
            />
          </div>

          {allUsersLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-wt-text-muted" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-wt-text-muted">
              <Users className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm">{searchQuery ? 'No users match your search' : 'No users'}</p>
            </div>
          ) : (
            <div className="rounded-lg border border-wt-border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-wt-bg-secondary text-wt-text-muted text-xs uppercase tracking-wider">
                    <th className="text-left px-4 py-2 font-medium">Email</th>
                    <th className="text-left px-4 py-2 font-medium">Role</th>
                    <th className="text-left px-4 py-2 font-medium">Org</th>
                    <th className="text-left px-4 py-2 font-medium">Status</th>
                    <th className="text-left px-4 py-2 font-medium">Signed Up</th>
                    <th className="text-left px-4 py-2 font-medium">Last Sign In</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u.user_id} className="border-t border-wt-border hover:bg-wt-bg-secondary/50">
                      <td className="px-4 py-3">
                        <div>
                          <span className="font-mono text-wt-text-primary">{u.email ?? u.user_id}</span>
                          {u.full_name && (
                            <p className="text-xs text-wt-text-muted">{u.full_name}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Badge variant="secondary" className="text-[10px]">{u.role}</Badge>
                          {u.permission_role !== 'none' && (
                            <Badge
                              variant={u.permission_role === 'owner' ? 'default' : 'secondary'}
                              className={`text-[10px] ${u.permission_role === 'owner' ? 'bg-wt-green-primary/20 text-wt-green-primary' : ''}`}
                            >
                              {u.permission_role}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-wt-text-muted">
                        {u.org_name ?? '—'}
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          variant={u.approved ? 'default' : 'secondary'}
                          className={`text-[10px] ${u.approved ? 'bg-wt-green-primary/20 text-wt-green-primary' : 'bg-wt-amber-primary/20 text-wt-amber-primary'}`}
                        >
                          {u.approved ? 'Approved' : 'Pending'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-wt-text-muted font-mono text-xs">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-wt-text-muted font-mono text-xs">
                        {u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleDateString() : 'Never'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </AdminTabShell>
  );
}
